import { Route } from '@angular/router';
import { AppRoutes } from '@cnp/common';
import { AuthenticationGuard } from './shared/services/guards/authentication.guard';
import { RouteAccessGuard } from './shared/services/guards/route-access.guard';
import { HomeRedirectGuard } from './shared/services/guards/home-redirect.guard';
import { clinicalCaseResolver } from './shared/resolvers/clinical-case.resolver';

export const appRoutes: Route[] = [
  {
    path: AppRoutes['HOME'].fullPath,
    pathMatch: 'full',
    canActivate: [HomeRedirectGuard],
    data: { routeKey: 'HOME' },
    loadComponent: () =>
      import('./pages/home/home.component').then((c) => c.HomeComponent),
  },
  {
    path: AppRoutes['GROUPS'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'GROUPS' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/groups/groups.component').then((c) => c.GroupsComponent),
  },
  {
    path: AppRoutes['GROUP'].fullPath,
    canActivate: [RouteAccessGuard],
    data: { routeKey: 'GROUP' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/group/group.component').then((c) => c.GroupComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: AppRoutes['GROUP_FEED'].childPath,
      },
      {
        path: AppRoutes['GROUP_FEED'].childPath,
        pathMatch: 'full',
        data: { routeKey: 'GROUP_FEED' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/group/group-feed/group-feed.component').then(
            (c) => c.GroupFeedComponent
          ),
      },
      {
        path: AppRoutes['GROUP_MEMBERS'].childPath,
        data: { routeKey: 'GROUP_MEMBERS' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/group/group-members/group-members.component').then(
            (c) => c.GroupMembersComponent
          ),
      },
      {
        path: AppRoutes['GROUPS_CLINICAL_CASES'].childPath,
        data: { routeKey: 'GROUPS_CLINICAL_CASES' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/group/group-clinical-cases/group-clinical-cases.component'
          ).then((c) => c.GroupClinicalCasesComponent),
      },
      {
        path: AppRoutes['GROUP_CLINICAL_CASE'].childPath,
        data: { routeKey: 'GROUP_CLINICAL_CASE' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/group/group-clinical-case/group-clinical-case.component'
          ).then((c) => c.GroupClinicalCaseComponent),
      },
      {
        path: AppRoutes['GROUP_ABOUT'].childPath,
        data: { routeKey: 'GROUP_ABOUT' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/group/group-about/group-about.component').then(
            (c) => c.GroupAboutComponent
          ),
      },
    ],
  },
  {
    path: AppRoutes['CLINICAL_CASES'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'CLINICAL_CASES' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/clinical-cases/clinical-cases.component').then(
        (c) => c.ClinicalCasesComponent
      ),
  },
  {
    path: AppRoutes['CLINICAL_CASE'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'CLINICAL_CASE' },
    resolve: { clinicalCase: clinicalCaseResolver },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/clinical-case/clinical-case.component').then(
        (c) => c.ClinicalCaseComponent
      ),
  },
  {
    path: AppRoutes['VIDEOS'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'VIDEOS' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/videos/videos.component').then((c) => c.VideosComponent),
  },
  {
    path: AppRoutes['VIDEO'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'VIDEO' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/video/video.component').then((c) => c.VideoComponent),
  },
  {
    path: AppRoutes['HUB_VIDEO'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'HUB_VIDEO' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/video/video.component').then((c) => c.VideoComponent),
  },
  {
    path: AppRoutes['COMMENT'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'COMMENT' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/comment/comment.component').then(
        (c) => c.CommentComponent
      ),
  },
  {
    path: AppRoutes['THREAD'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'THREAD' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/thread/thread.component').then((c) => c.ThreadComponent),
  },
  {
    path: AppRoutes['HUBS'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'HUBS' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/hubs/hubs.component').then((c) => c.HubsComponent),
  },
  {
    path: AppRoutes['FEED'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'FEED' },
    loadComponent: () =>
      import('./pages/feed/feed.component').then((c) => c.FeedComponent),
  },
  {
    path: AppRoutes['CHAT'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'CHAT' },
    loadComponent: () =>
      import('./pages/chat/chat.component').then((c) => c.ChatComponent),
  },
  {
    path: AppRoutes['CHAT_ROOM'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'CHAT_ROOM' },
    loadComponent: () =>
      import('./pages/chat/chat.component').then((c) => c.ChatComponent),
  },
  {
    path: AppRoutes['PROFILE'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'PROFILE' },
    loadComponent: () =>
      import('./pages/profile/profile.component').then(
        (c) => c.ProfileComponent
      ),
    children: [
      {
        path: AppRoutes['MY_PROFILE'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'MY_PROFILE' },
        loadComponent: () =>
          import(
            './pages/profile/components/my-profile/my-profile.component'
          ).then((c) => c.MyProfileComponent),
      },
      {
        path: AppRoutes['MY_CONNECTIONS'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'MY_CONNECTIONS' },
        loadComponent: () =>
          import(
            './pages/profile/components/my-connections/my-connections.component'
          ).then((c) => c.MyConnectionsComponent),
      },
      {
        path: AppRoutes['MY_FOLLOWERS'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'MY_FOLLOWERS' },
        loadComponent: () =>
          import(
            './pages/profile/components/my-followers/my-followers.component'
          ).then((c) => c.MyFollowersComponent),
      },
      {
        path: AppRoutes['MY_NOTIFICATIONS'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'MY_NOTIFICATIONS' },
        loadComponent: () =>
          import(
            './pages/profile/components/notifications/notifications.component'
          ).then((c) => c.NotificationsComponent),
      },
    ],
  },
  {
    path: AppRoutes['OPPORTUNITIES'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'OPPORTUNITIES' },
    loadComponent: () =>
      import('./pages/opportunities/opportunities.component').then(
        (c) => c.OpportunitiesComponent
      ),
  },
  {
    path: AppRoutes['EVENTS'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'EVENTS' },
    loadComponent: () =>
      import('./pages/events/events.component').then((c) => c.EventsComponent),
  },
  {
    path: AppRoutes['EVENT'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'EVENT' },
    loadComponent: () =>
      import('./pages/event/event.component').then((c) => c.EventPageComponent),
  },
  {
    path: AppRoutes['COURSES'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'COURSES' },
    loadComponent: () =>
      import('./pages/courses/courses.component').then(
        (c) => c.CoursesComponent
      ),
  },
  {
    path: AppRoutes['HUB'].fullPath,
    canActivate: [RouteAccessGuard],
    data: { routeKey: 'HUB' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/hub/hub.component').then((c) => c.HubComponent),
    children: [
      {
        path: AppRoutes['HUB_FEED'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_FEED' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/hub/components/hub-feed/hub-feed.component').then(
            (c) => c.HubFeedComponent
          ),
      },
      // Public on purpose: this is where a visitor who isn't a member lands.
      {
        path: AppRoutes['HUB_HOME'].childPath,
        data: { routeKey: 'HUB_HOME' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/hub/components/hub-home/hub-home.component').then(
            (c) => c.HubHomeComponent
          ),
      },
      {
        path: AppRoutes['HUB_COURSES'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_COURSES' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-courses/hub-courses.component'
          ).then((c) => c.HubCoursesComponent),
        children: [
          {
            path: AppRoutes['HUB_COURSES_LIST'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_COURSES_LIST' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './shared/components/courses/courses-list/courses-list.component'
              ).then((c) => c.CoursesListComponent),
          },
          {
            path: AppRoutes['HUB_COURSE'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_COURSE' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './shared/components/courses/course/course.component'
              ).then((c) => c.CourseComponent),
          },
          {
            path: AppRoutes['HUB_CLASSROOM'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_CLASSROOM' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './shared/components/courses/classroom/classroom.component'
              ).then((c) => c.ClassroomComponent),
          },
        ],
      },
      {
        path: AppRoutes['HUB_EVENTS'].childPath,
        data: { routeKey: 'HUB_EVENTS' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/hub/components/hub-events/hub-event.component').then(
            (c) => c.HubEventComponent
          ),
        children: [
          {
            path: AppRoutes['HUB_EVENTS_LIST'].childPath,
            pathMatch: 'full',
            data: { routeKey: 'HUB_EVENTS_LIST' },
            loadComponent: () =>
              import(
                './pages/hub/components/hub-events/events/events.component'
              ).then((c) => c.EventsComponent),
          },
          {
            path: AppRoutes['HUB_EVENT'].childPath,
            data: { routeKey: 'HUB_EVENT' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-events/event/event.component'
              ).then((c) => c.EventComponent),
          },
        ],
      },
      {
        path: AppRoutes['HUB_VIDEOS'].childPath,
        canActivate: [AuthenticationGuard],
        children: [
          {
            path: AppRoutes['HUB_VIDEOS_LIST'].childPath,
            pathMatch: 'full',
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_VIDEOS_LIST' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-videos/hub-videos.component'
              ).then((c) => c.HubVideosComponent),
          },
        ],
      },

      {
        path: AppRoutes['HUB_GROUPS'].childPath,
        data: { routeKey: 'HUB_GROUPS' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/hub/components/hub-groups/hub-groups.component').then(
            (c) => c.HubGroupsComponent
          ),
      },
      {
        path: AppRoutes['HUB_GROUP'].childPath,
        data: { routeKey: 'HUB_GROUP' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import('./pages/hub/components/hub-group/hub-group.component').then(
            (c) => c.HubGroupComponent
          ),
        children: [
          {
            path: AppRoutes['HUB_GROUP_FEED'].childPath,
            pathMatch: 'full',
            data: { routeKey: 'HUB_GROUP_FEED' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import('./pages/group/group-feed/group-feed.component').then(
                (c) => c.GroupFeedComponent
              ),
          },
          {
            path: AppRoutes['HUB_GROUP_ABOUT'].childPath,
            data: { routeKey: 'HUB_GROUP_ABOUT' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import('./pages/group/group-about/group-about.component').then(
                (c) => c.GroupAboutComponent
              ),
          },
          {
            path: AppRoutes['HUB_GROUPS_CLINICAL_CASES'].childPath,
            data: { routeKey: 'HUB_GROUPS_CLINICAL_CASES' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-clinical-cases/hub-clinical-cases.component'
              ).then((c) => c.HubClinicalCasesComponent),
          },
          {
            path: AppRoutes['HUB_GROUP_CLINICAL_CASE'].childPath,
            data: { routeKey: 'HUB_GROUP_CLINICAL_CASE' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/group/group-clinical-case/group-clinical-case.component'
              ).then((c) => c.GroupClinicalCaseComponent),
          },
          {
            path: AppRoutes['HUB_GROUP_MEMBERS'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_GROUP_MEMBERS' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-members/hub-members.component'
              ).then((c) => c.HubMembersComponent),
          },
        ],
      },
      {
        path: AppRoutes['HUB_CLINICAL_CASES'].childPath,
        data: { routeKey: 'HUB_CLINICAL_CASES' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-clinical-cases/hub-clinical-cases.component'
          ).then((c) => c.HubClinicalCasesComponent),
      },
      {
        path: AppRoutes['HUB_CLINICAL_CASE'].childPath,
        data: { routeKey: 'HUB_CLINICAL_CASE' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-clinical-case/hub-clinical-case.component'
          ).then((c) => c.HubClinicalCaseComponent),
      },
      // Members & About now live under the Community section. Redirect the old
      // flat paths so existing deep links keep working.
      {
        path: AppRoutes['HUB_MEMBERS'].childPath,
        redirectTo: AppRoutes['HUB_COMMUNITY_MEMBERS'].childPath,
        pathMatch: 'full',
      },
      {
        path: AppRoutes['HUB_ABOUT'].childPath,
        redirectTo: AppRoutes['HUB_COMMUNITY_ABOUT'].childPath,
        pathMatch: 'full',
      },
      {
        path: AppRoutes['HUB_COMMUNITY_MEMBERS'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_COMMUNITY_MEMBERS' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-community/hub-community-members/hub-community-members.component'
          ).then((c) => c.HubCommunityMembersComponent),
      },
      {
        path: AppRoutes['HUB_COMMUNITY_ABOUT'].childPath,
        data: { routeKey: 'HUB_COMMUNITY_ABOUT' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-community/hub-community-about/hub-community-about.component'
          ).then((c) => c.HubCommunityAboutComponent),
      },
      {
        path: AppRoutes['HUB_COMMUNITY'].childPath,
        data: { routeKey: 'HUB_COMMUNITY' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-community/hub-community.component'
          ).then((c) => c.HubCommunityComponent),
      },
      {
        path: AppRoutes['HUB_RESOURCES_FILES'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_RESOURCES_FILES' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-resources/hub-files/hub-files.component'
          ).then((c) => c.HubFilesComponent),
      },
      {
        path: AppRoutes['HUB_RESOURCES'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_RESOURCES' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        loadComponent: () =>
          import(
            './pages/hub/components/hub-resources/hub-resources.component'
          ).then((c) => c.HubResourcesComponent),
      },
      {
        path: AppRoutes['HUB_PROGRAMS'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_PROGRAMS' },
        children: [
          {
            path: AppRoutes['HUB_PROGRAMS_LIST'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_PROGRAMS_LIST' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-programs/components/hub-programs/hub-programs.component'
              ).then((c) => c.HubProgramsComponent),
          },
          {
            path: AppRoutes['HUB_PROGRAM'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_PROGRAM' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-programs/components/hub-program/hub-program.component'
              ).then((c) => c.HubProgramComponent),
          },
          {
            path: AppRoutes['HUB_PROGRAM_ANSWERED'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_PROGRAM_ANSWERED' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-programs/components/program-answered/program-answered.component'
              ).then((c) => c.ProgramAnsweredComponent),
          },
        ],
      },
      // {
      //   path: AppRoutes['HUB_PROGRAMS'].childPath,
      //   canActivate: [AuthenticationGuard],
      //   data: { routeKey: 'HUB_PROGRAMS' },
      //   runGuardsAndResolvers: 'paramsOrQueryParamsChange',
      {
        path: AppRoutes['HUB_EVALUATIONS'].childPath,
        canActivate: [AuthenticationGuard],
        data: { routeKey: 'HUB_EVALUATIONS' },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        children: [
          {
            path: AppRoutes['HUB_EVALUATIONS_LIST'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_EVALUATIONS_LIST' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-evaluations/components/evaluations/evaluations.component'
              ).then((c) => c.EvaluationsComponent),
          },
          {
            path: AppRoutes['HUB_EVALUATION'].childPath,
            canActivate: [AuthenticationGuard],
            data: { routeKey: 'HUB_EVALUATION' },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import(
                './pages/hub/components/hub-evaluations/components/evaluation/evaluation.component'
              ).then((c) => c.EvaluationComponent),
          },
        ],
      },
    ],
  },
  {
    path: AppRoutes['SIGN_UP'].fullPath,
    loadComponent: () =>
      import('./pages/signup/signup.component').then((c) => c.SignupComponent),
  },
  {
    path: AppRoutes['LOGIN'].fullPath,
    loadComponent: () =>
      import('./pages/login/login.component').then((c) => c.LoginComponent),
  },
  {
    path: AppRoutes['RECOVERY_PASSWORD'].fullPath,
    loadComponent: () =>
      import('./pages/recovery-password/recovery-password.component').then(
        (c) => c.RecoveryPasswordComponent
      ),
  },
  {
    path: AppRoutes['SEARCH'].fullPath,
    canActivate: [AuthenticationGuard, RouteAccessGuard],
    data: { routeKey: 'SEARCH' },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    loadComponent: () =>
      import('./pages/search/search.component').then((c) => c.SearchComponent),
  },
  {
    path: AppRoutes['NOT_FOUND'].fullPath,
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then(
        (c) => c.NotFoundComponent
      ),
  },
  {
    path: '**',
    redirectTo: AppRoutes['NOT_FOUND'].fullPath,
  },
];
